import { useState } from "react";

import { AlertTriangle } from "lucide-react";

import { PaywallDialog } from "src/components/PaywallDialog/PaywallDialog";
import { Button } from "src/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { useAuth } from "src/lib/auth";
import { cn } from "src/lib/utils";

const BILLING_REASON_PATTERN = /billing/i;

/**
 * Returns whether the store is disconnected because billing is unresolved.
 *
 * @param {object} store
 * @returns {boolean}
 */
export const isStoreSyncPausedForBilling = (store) =>
  store?.isConnected === false &&
  BILLING_REASON_PATTERN.test(store?.connectionStatusReason || "");

const getBillingOwner = (store) =>
  store?.users?.find((member) => member.isBillingUser)?.user || null;

export function StoreSyncPausedNotice({
  store,
  variant = "badge",
  className,
  side = "bottom",
  align = "start",
  showAction = true,
  preventParentInteraction = false,
}) {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);

  if (!isStoreSyncPausedForBilling(store)) {
    return null;
  }

  const billingOwner = getBillingOwner(store);
  const isBillingUser = store?.users?.some(
    (member) => member.isBillingUser && member.user?.id === currentUser?.id
  );
  const ownerLabel =
    billingOwner?.name || billingOwner?.email || "the billing owner";
  const description = isBillingUser
    ? "Syncing is paused for this store, so dashboard numbers will fall out of sync until you add a payment method."
    : "Syncing is paused for this store, so dashboard numbers will fall out of sync until a payment method is added.";

  const stopParentInteraction = (event) => {
    if (!preventParentInteraction) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const tooltipContent = (
    <div className="space-y-1">
      <div className="font-medium">Sync paused</div>
      <p>{description}</p>
      {!isBillingUser && (
        <div className="pt-1 text-[11px] opacity-80">
          Billing owner: {ownerLabel}
        </div>
      )}
    </div>
  );

  if (!showAction) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label="Sync paused"
            className={cn(
              variant === "badge"
                ? "inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300"
                : "text-amber-600 dark:text-amber-400 inline-flex size-6 cursor-pointer items-center justify-center rounded-md",
              className
            )}
          >
            <AlertTriangle
              className={variant === "badge" ? "size-3.5" : "size-4"}
            />
            {variant === "badge" && (
              <span className="hidden whitespace-nowrap md:inline">
                Sync paused
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          sideOffset={8}
          className="max-w-80 text-left"
        >
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Sync paused"
          onClick={stopParentInteraction}
          onPointerDown={stopParentInteraction}
          className={cn(
            variant === "badge"
              ? "inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300"
              : "text-amber-600 hover:bg-accent hover:text-amber-600 dark:text-amber-400 inline-flex size-6 cursor-pointer items-center justify-center rounded-md transition-colors",
            className
          )}
        >
          <AlertTriangle
            className={variant === "badge" ? "size-3.5" : "size-4"}
          />
          {variant === "badge" && (
            <span className="hidden whitespace-nowrap md:inline">
              Sync paused
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side={side} align={align} className="w-80 space-y-3">
        <div className="space-y-1">
          <div className="text-sm font-semibold">Sync paused</div>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>

        {!isBillingUser && (
          <div className="text-muted-foreground text-xs">
            Billing owner: {ownerLabel}
          </div>
        )}

        {showAction && isBillingUser && (
          <Button
            size="sm"
            className="w-full"
            onClick={() => {
              setOpen(false);
              setPaywallOpen(true);
            }}
          >
            Add Payment Method
          </Button>
        )}
      </PopoverContent>

      {showAction && isBillingUser && (
        <PaywallDialog
          source="billingPaused"
          open={paywallOpen}
          onOpenChange={setPaywallOpen}
        />
      )}
    </Popover>
  );
}
